import aliasData from "./global-aliases.json";

const WHOLE_STRING: Record<string, string> = aliasData.wholeString;
const TOKEN_MAP: Record<string, string> = aliasData.tokens;

/**
 * Words that carry no distinguishing information inside a proper name. Used so
 * "emirates" can stand in for "United Arab Emirates" while "united" cannot.
 */
export const GENERIC_TOKENS = new Set([
  "the",
  "of",
  "a",
  "an",
  "and",
  "de",
  "du",
  "la",
  "le",
  "el",
  "los",
  "las",
  "il",
  "di",
  "van",
  "von",
  "republic",
  "democratic",
  "peoples",
  "people",
  "united",
  "state",
  "states",
  "kingdom",
  "federation",
  "commonwealth",
  "union",
  "province",
  "region",
  "territory",
  "city",
  "town",
  "island",
  "islands",
  "isle",
  "north",
  "south",
  "east",
  "west",
  "northern",
  "southern",
  "eastern",
  "western",
  "new",
  "old",
  "great",
  "greater",
  "grand",
  "saint",
  "san",
  "sao",
  "santa",
  "mount",
  "mountain",
  "lake",
  "river",
  "sea",
  "gulf",
  "bay",
]);

/**
 * Definite articles only.
 *
 * "a" and "an" are deliberately absent. A leading "the" is noise — nobody
 * distinguishes "The Gambia" from "Gambia" — but a leading "a" is frequently
 * the meaningful part of the name: blood group A, vitamin A, A Coruña, A minor.
 * Stripping it turned a guess of "A positive" into "positive", which then came
 * back ambiguous against every other blood group on the board.
 */
const LEADING_ARTICLES = /^(the|le|la|les|el|los|las|il|lo|der|die|das)\s+/;

/**
 * Pull the emoji out of a string, before normalization deletes them.
 *
 * An answer like "Face with Tears of Joy 😂" is normally guessed by typing the
 * emoji itself, but the a-z filter reduces a bare 😂 to an empty string, so it
 * could never match anything. Variation selectors and skin-tone modifiers are
 * dropped so ❤ and ❤️ are the same answer.
 */
export function extractEmoji(input: string): string[] {
  const found = input.match(/\p{Extended_Pictographic}/gu) ?? [];
  const seen = new Set<string>();
  for (const raw of found) {
    const bare = raw.replace(/[︎️\u{1F3FB}-\u{1F3FF}]/gu, "");
    if (bare) seen.add(bare);
  }
  return [...seen];
}

/** Strip accents, punctuation, casing and spacing noise. */
export function baseNormalize(input: string): string {
  return (
    input
      .normalize("NFD")
      // Combining marks: é -> e, ü -> u, ñ -> n
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      // Danish/Norwegian ø and friends survive NFD, so map them explicitly.
      .replace(/ø/g, "o")
      .replace(/æ/g, "ae")
      .replace(/å/g, "a")
      .replace(/ß/g, "ss")
      .replace(/đ|ð/g, "d")
      .replace(/þ/g, "th")
      .replace(/ł/g, "l")
      // Letters that are not a base letter plus a mark, so NFD leaves them
      // whole and the a-z filter below would delete them outright. Turkish
      // dotless ı was turning "Barış" into "bar s".
      .replace(/ı/g, "i")
      .replace(/œ/g, "oe")
      .replace(/ŋ/g, "n")
      .replace(/ĳ/g, "ij")
      .replace(/ſ/g, "s")
      .replace(/&/g, " and ")
      // Apostrophes join words: d'Ivoire -> d ivoire, O'Brien -> o brien
      .replace(/['’`´]/g, " ")
      .replace(/[^a-z0-9]+/g, " ")
      .trim()
      .replace(/\s+/g, " ")
  );
}

/** Expand per-token abbreviations (st -> saint) and drop noise tokens. */
function expandTokens(text: string): string {
  return text
    .split(" ")
    .map((t) => (t in TOKEN_MAP ? TOKEN_MAP[t]! : t))
    .filter((t) => t.length > 0)
    .join(" ")
    .trim();
}

export interface Normalized {
  /** Fully normalized text after alias resolution. */
  text: string;
  tokens: string[];
  /** Tokens minus generic filler — what actually identifies the answer. */
  distinctive: string[];
}

export function normalize(
  input: string,
  opts: { stripArticles?: boolean } = {},
): Normalized {
  let text = baseNormalize(input);
  if (opts.stripArticles !== false) text = text.replace(LEADING_ARTICLES, "");
  text = expandTokens(text);

  // Whole-string aliases run after expansion so "st kitts" resolves via "saint kitts"
  // as well as its raw form.
  if (text in WHOLE_STRING) text = WHOLE_STRING[text]!;
  else {
    const raw = baseNormalize(input);
    if (raw in WHOLE_STRING) text = WHOLE_STRING[raw]!;
  }

  const tokens = text.split(" ").filter(Boolean);
  const distinctive = tokens.filter(
    (t) => !GENERIC_TOKENS.has(t) && t.length > 1,
  );
  return { text, tokens, distinctive };
}

/**
 * Squash runs of the same letter so "phillippines" and "philippines" collapse to
 * the same key. Deliberately lossy — only used as one signal among several.
 */
export function collapseRepeats(text: string): string {
  return text.replace(/(.)\1+/g, "$1");
}

/** Word-order-insensitive key: "congo democratic republic" == "democratic republic congo". */
export function sortedKey(tokens: string[]): string {
  return [...tokens].sort().join(" ");
}
